/**
 * FileAutocomplete — dropdown for @ file autocomplete in chat input
 *
 * Controlled component. Parent (ChatPanel) owns filtered/sorted items and selected index.
 * Position: absolute, anchored above the input.
 */
import { useRef, useEffect } from 'react'
import { File, Folder, Loader2 } from 'lucide-react'

interface Props {
  visible: boolean
  items: string[]
  selectedIndex: number
  loading: boolean
  onSelect: (path: string) => void
  onDismiss: () => void
}

export function FileAutocomplete({ visible, items, selectedIndex, loading, onSelect, onDismiss }: Props) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Click-outside dismiss
  useEffect(() => {
    if (!visible) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onDismiss()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [visible, onDismiss])

  // Scroll selected item into view
  useEffect(() => {
    if (!visible) return
    const container = menuRef.current
    if (!container) return
    const selected = container.querySelector('[data-selected="true"]')
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }, [visible, selectedIndex])

  if (!visible) return null

  return (
    <div
      ref={menuRef}
      className="absolute bottom-full left-0 right-0 mb-1 max-h-[260px] overflow-y-auto rounded-md border themed-border themed-bg-secondary shadow-lg z-50"
    >
      {loading && items.length === 0 ? (
        <div className="flex items-center gap-2 px-3 py-2 text-xs themed-text-muted">
          <Loader2 size={12} className="animate-spin" />
          Loading files...
        </div>
      ) : items.length === 0 ? (
        <div className="px-3 py-2 text-xs themed-text-muted">
          No matching files
        </div>
      ) : (
        items.map((path, idx) => {
          const isFolder = path.endsWith('/')
          const cleanPath = isFolder ? path.slice(0, -1) : path
          const lastSlash = cleanPath.lastIndexOf('/')
          const name = lastSlash >= 0 ? cleanPath.slice(lastSlash + 1) : cleanPath
          const dir = lastSlash >= 0 ? cleanPath.slice(0, lastSlash) : ''
          const isSelected = idx === selectedIndex

          return (
            <button
              key={path}
              data-selected={isSelected}
              onClick={() => onSelect(path)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                isSelected ? 'themed-bg-tertiary themed-text' : 'themed-text hover:themed-bg-tertiary'
              }`}
            >
              {isFolder ? (
                <Folder size={12} className="shrink-0 themed-text-muted" />
              ) : (
                <File size={12} className="shrink-0 themed-text-muted" />
              )}
              <span className="truncate font-medium">{name}</span>
              {dir && (
                <span className="truncate themed-text-muted ml-auto text-[10px]">{dir}</span>
              )}
            </button>
          )
        })
      )}
    </div>
  )
}
